import { parse, stringify } from 'query-string';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { PREDICTED_PROCESSING_TIME_INTERVALS } from '@Pages/RetroSynthesis/constants';

// 'query-string' version 5.1.1 does not support comma separator for the arrays
// 'query-string' version ^6  does not support IE
const parseOption = {};

export const objectToUrlParams = (params) => stringify(params, parseOption);

export const urlParamToObject = (param) => parse(param, parseOption);

export const wrapToArray = (value) => (!isArray(value) ? [value] : value);

export const setUrlParamByName = (paramName, paramValue) => stringify({ [paramName]: paramValue });

export const getUrlParams = (location) => parse(location && location.search, parseOption);

export const getUrlParamByName = (location, paramName) => parse(location && location.search, parseOption)[paramName];

export const getUrlParamsArrayByName = (location, paramName) => {
  const result = getUrlParamByName(location, paramName);
  return !result || isArray(result) ? result : [result];
};

export const isEqualLocation = (a, b) => a && b && a.pathname === b.pathname && isEqual(parse(a.search), parse(b.search));

export const mapSortStringToObject = (sortString) => {
  if (!sortString) {
    return;
  }

  const [property, direction] = sortString.split(',');
  // eslint-disable-next-line consistent-return
  return { property, direction };
};

export const mapSortObjectToString = (sortObject) => {
  if (!sortObject) {
    return;
  }

  const { property, direction } = sortObject;
  // eslint-disable-next-line consistent-return
  return [property, direction].join(',');
};

const getStorage = (isSessionStorage) => (isSessionStorage ? sessionStorage : localStorage);

export const getValueFromStorage = (key, isSessionStorage = false) => {
  const savedItem = getStorage(isSessionStorage).getItem(key);
  if (!savedItem) return null;
  const data = JSON.parse(savedItem);
  return !isEmpty(data) ? data : null;
};

export const removeValueFromStorage = (key, isSessionStorage = false) => {
  getStorage(isSessionStorage).removeItem(key);
};

export const putValueToStorage = (key, value, isSessionStorage = false) => {
  const serializedData = JSON.stringify(value);
  getStorage(isSessionStorage).setItem(key, serializedData);
};

export const trimUptoTwoDecimals = (confidenceScore) => {
  const confidence = String(confidenceScore);
  if (confidence.includes('.')) {
    return parseFloat(confidence.match(/^-?\d+(?:\.\d{0,2})?/)[0]);
  }
  return confidenceScore;
};

function getPredictedProcessingTimeInterval(processingTime, intervals = PREDICTED_PROCESSING_TIME_INTERVALS) {
  // need to round up time for existing projects with customized time between new time intervals
  if (!processingTime || processingTime > intervals[intervals.length - 1].value) {
    Logger.warn('RT project params error - processing time is invalid', processingTime);
    // error parsing - return default
    return intervals[0];
  }
  return intervals.find((interval) => processingTime <= interval.value);
}

export function getPredictedProcessingTimeIntervalUpdatedValue(processingTime, intervals) {
  return getPredictedProcessingTimeInterval(processingTime, intervals).value;
}

export function getPredictedProcessingTimeLabelByValue(processingTime, intervals) {
  return getPredictedProcessingTimeInterval(processingTime, intervals).label;
}

export const scrollTop = (target = window, smooth = true) =>
  setTimeout(() => {
    if (smooth) {
      try {
        target.scrollTo({ top: 0.1, behavior: 'smooth' });
        // eslint-disable-next-line sonarjs/no-ignored-exceptions,no-unused-vars
      } catch (error) {
        target.scrollTo(0.1, 0);
      }
    } else {
      target.scrollTo(0.1, 0);
    }
  });

export const scrollToPosition = (table, position, id) => {
  setTimeout(() => {
    table.scrollTo({ top: position, behavior: 'smooth' });
    const target = document.getElementById(id);
    if (target) {
      target.focus();
    }
  });
};

export const focusOnElement = (id) => {
  setTimeout(() => {
    const target = document.getElementById(id);
    if (target) {
      target.focus();
    }
  });
};
